import { fromJS, List, Map } from 'immutable';
import { dispatch } from 'nike-re-framejs';
import { stats2Client } from '../../common/axios/stats2Client';
import { cakeCmsClient } from '../../common/axios/cakeCmsClient';
import { nikeoneClient } from '../../common/axios/nikeoneClient';
import { makeMatchesRequestParam, makeTournamentsRequestParam } from '../turboLive/utils';
import { Delta, PushEvent, PushPayload } from '../turboLive/dto/PushEnvelope';
import { MatchTopLiveSnapshot } from '../turboLive/dto/SnapshotEnvelope';
import { Bet, LiveMatch } from '../turboLive/dto';
import * as diff from 'nike-ui/src/common/push/diff';
import { TOP_5_BETS } from './constants';
import { keyBy } from 'lodash';
import { getSportNameById } from '../../common/utils/utils';

export const fetch10NajTop5 = () => stats2Client.get('/top-ranking').then(data => dispatch(statsLoaded(data)));

export const fetch10NajLive = () =>
    stats2Client
        .get('/top-selections', {
            params: {
                gameType: 'Live',
                limit: 10
            }
        })
        .then(data => dispatch(naj10LiveLoaded(data)));

export function fetchMarketStatistics(matchIds) {
    const uniqueIds = [...new Set(matchIds)];
    uniqueIds.forEach(matchId => {
        const params = { gameType: 'Prematch' };
        stats2Client.get(`/${matchId}`, { params }).then(data => {
            dispatch(db => db.setIn(['market-statistics', 'betbook', matchId], fromJS(data)));
        });
    });
}

export const fetchTop5Tournaments = () =>
    cakeCmsClient.get('/tournaments-data/hp-top-tournaments').then(data => dispatch(cmsTournamentsLoaded(data)));

export const loadMatches = (matchIds, dispatchFunc) => {
    const request = makeMatchesRequestParam(matchIds);
    dispatch(matchesLoading);
    return nikeoneClient.get('/v1/boxes/sport-events', { params: request }).then(data => dispatch(dispatchFunc(data)));
};

export const loadTournaments = tournamentsIds => {
    const request = makeTournamentsRequestParam(tournamentsIds);
    dispatch(matchesLoading);
    return nikeoneClient
        .get('/v1/boxes/tournaments', { params: request })
        .then(data => dispatch(n1TournamentsLoaded(data)));
};

const statsLoaded = json => db =>
    db
        .setIn(
            ['market-statistics', 'top-ranking', 'top5sports'],
            Map(
                json.top5.map(sport => [
                    `${sport.sportId}`,
                    Map({
                        sportId: sport.sportId,
                        sportName: getSportNameById(sport.sportId, false),
                        topEvents: fromJS(sport.topEvents)
                    })
                ])
            )
        )
        .setIn(['market-statistics', 'top-ranking', 'top10', 'bets'], fromJS(json.naj10))
        .setIn(['market-statistics', 'top-ranking', 'top10Live', 'bets'], fromJS(json.naj10Live))
        .setIn(['market-statistics', 'top-ranking', 'loaded'], true);

const naj10LiveLoaded = json => db =>
    db.setIn(['market-statistics', 'top-ranking', 'top10Live', 'bets'], fromJS(json.topEventsSelections));

export const topEventsLoaded = json => db => {
    const newBets = fromJS(keyBy(json.bets, 'betId'));
    const matches = fromJS(keyBy(json.sportEvents, 'sportEventId'));
    if (!matches || matches.size === 0) {
        return db;
    }

    return db
        .updateIn(TOP_5_BETS, Map(), prevBets =>
            prevBets.concat(newBets.filterNot(nb => prevBets.some(pb => pb.get('betId') === nb.get('betId'))))
        )
        .setIn(
            ['market-statistics', 'top-ranking', 'top5sportMatches'],
            matches.groupBy(m => m.get('sportId'))
        )
        .setIn(['market-statistics', 'top-ranking', 'matchesLoaded'], true);
};

export const cmsTournamentsLoaded = json => db =>
    db
        .setIn(
            ['market-statistics', 'top-ranking', 'top5tournaments'],
            Map(
                json.map(t => [
                    `${t.tournament_id}`,
                    Map({
                        tournamentId: t.tournament_id,
                        sportId: t.sport_id,
                        sportName: getSportNameById(t.sport_id, false),
                        showAll: false
                    })
                ])
            )
        )
        .setIn(['market-statistics', 'top-ranking', 'top5tournamentsIds'], List(json.map(t => t.tournament_id)))
        .setIn(['market-statistics', 'top-ranking', 'cmsTournamentsLoaded'], true);

const n1TournamentsLoaded = json => db => {
    const newBets = fromJS(keyBy(json.bets, 'betId'));
    const matches = fromJS(keyBy(json.sportEvents, 'sportEventId')).filter(
        match => match?.get('matchTemplate') !== 'RACE'
    );

    if (!matches || matches.size === 0) {
        return db;
    }

    return db
        .setIn(
            ['market-statistics', 'top-ranking', 'top5tournamentMatches'],
            matches?.sortBy(match => Date.parse(match?.get('expiration'))).groupBy(m => m.get('tournamentId'))
        )
        .updateIn(TOP_5_BETS, Map(), prevBets =>
            prevBets.concat(newBets.filterNot(nb => prevBets.some(pb => pb.get('betId') === nb.get('betId'))))
        )
        .setIn(['market-statistics', 'top-ranking', 'tournamentsLoaded'], true);
};

const matchesLoading = db => db.setIn(['market-statistics', 'top-ranking', 'matchesLoaded'], false);

export const setTop10ActiveTab = activeTab => db =>
    db.setIn(['market-statistics', 'top-ranking', 'top10ActiveTab'], activeTab);

export const matchInfoSnapshot = (snapshot: MatchTopLiveSnapshot, matchId) => db => {
    return db.setIn(['market-statistics', 'top-ranking', 'top10Live', 'matches', matchId], fromJS(snapshot.match));
};

export const matchInfoPush = (push: PushPayload, matchId: string) => db => {
    return db.updateIn(['market-statistics', 'top-ranking', 'top10Live', 'matches', matchId], match =>
        onMatchInfoPush(push.e, push.d, match)
    );
};

export const onMatchInfoPush = (event: PushEvent, data: LiveMatch | Delta | { id: string }, match) => {
    switch (event) {
        case PushEvent.UPDATED:
            return diff.applyDelta((data as Delta).diff, match);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
};

export const betInfoSnapshot = (snapshot, betId) => db => {
    return db.setIn(['market-statistics', 'top-ranking', 'top10Live', 'betsLive', betId], fromJS(snapshot.bet));
};

export const betInfoPush = (push: PushPayload, betId) => db => {
    return db.updateIn(['market-statistics', 'top-ranking', 'top10Live', 'betsLive'], bets =>
        onBetInfoPush(push.e, push.d, bets)
    );
};

export const onBetInfoPush = (event: PushEvent, data: Bet | Delta | { id: string }, bets: Map<string, any>) => {
    switch (event) {
        case PushEvent.CREATED:
            return bets.set(data.id, fromJS(data));
        case PushEvent.UPDATED:
            return bets.update(data.id, existing => diff.applyDelta((data as Delta).diff, existing));
        case PushEvent.DELETED:
            return bets.delete(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
};

export const top10PrematchMatchesLoaded = json => db => {
    const matches = fromJS(keyBy(json?.sportEvents, 'sportEventId'));
    return db.setIn(['market-statistics', 'top-ranking', 'top10', 'matches'], matches);
};

export const top10LiveMatchesLoaded = json => db => {
    const matches = fromJS(keyBy(json?.sportEvents, 'sportEventId'));
    return db.setIn(['market-statistics', 'top-ranking', 'top10Live', 'matches'], matches);
};

export const topEventsBetbookMatchesLoaded = json => db => {
    const matches = fromJS(keyBy(json?.sportEvents, 'sportEventId'));
    return db.setIn(['betbook', 'topEvents', 'data', 'matches'], matches);
};

export const homepageInfocentrumSet = () => db =>
    db.setIn(['market-statistics', 'top-ranking', 'infocentrumSet'], true);

export const setShowAllBetsTournament = (tournamentId, show) => db =>
    db.setIn(['market-statistics', 'top-ranking', 'top5tournaments', `${tournamentId}`, 'showAll'], show);

export const deleteTopRanking = db => db.deleteIn(['market-statistics', 'top-ranking']);
